import React, { useState, useEffect, useRef } from 'react';
import { Upload, RefreshCw, AlertTriangle, X, CheckCircle, FileDown, Building2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  Box,
  CircularProgress,
  Button
} from '@mui/material';
import MainService from '../../services/MainService';

const ClientUpload = () => {
  const [clients, setClients] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadEmirates();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadEmirates = async () => {
    try {
      const response = await MainService.EmiratesInDropDown();
      setEmirates(response || []);
    } catch (err) {
      console.error('Error loading emirates:', err);
    }
  };

  const calculateSimilarity = (s1, s2) => {
    if (!s1 || !s2) return 0;
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    const longerLength = longer.length;
    
    if (longerLength === 0) return 1.0;
    
    const editDistance = getEditDistance(longer.toLowerCase(), shorter.toLowerCase());
    return (longerLength - editDistance) / longerLength;
  };

  const getEditDistance = (s1, s2) => {
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  const checkEmirate = async (client) => {
    if (!client.EmirateName) return;
    
    let bestMatch = null;
    let bestSimilarity = 0;

    emirates.forEach((emirate) => {
      const similarity = calculateSimilarity(emirate.Name, client.EmirateName);
      if (similarity > bestSimilarity) {
        bestSimilarity = similarity;
        bestMatch = emirate;
      }
    });

    if (bestMatch) {
      client.EmirateId = bestMatch.Id;
      await loadLocationsForClient(client);
    }
  };

  const loadLocationsForClient = async (client) => {
    if (!client.EmirateId) return;
    try {
      const response = await MainService.LocationByEmirateId(client.EmirateId);
      client.Locations = response || [];
      
      // Auto-match location if LocationName exists
      if (client.LocationName && client.Locations.length > 0) {
        let bestLocationMatch = null;
        let bestLocationSimilarity = 0;

        client.Locations.forEach((location) => {
          const similarity = calculateSimilarity(location.Name, client.LocationName);
          if (similarity > bestLocationSimilarity) {
            bestLocationSimilarity = similarity;
            bestLocationMatch = location;
          }
        });

        if (bestLocationMatch) {
          client.LocationId = bestLocationMatch.Id;
        }
      }
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const processedClients = jsonData.map((row) => ({
          Name: row.Name || '',
          MobileNo1: row.MobileNo1 || '',
          MobileNo2: row.MobileNo2 || '',
          TelNo: row.TelNo || '',
          Company: row.Company || '',
          DeliveryCharges: row.DeliveryCharges || '',
          EmailId: row.EmailId || '',
          BankName: row.BankName || '',
          BankAccNo: row.BankAccNo || '',
          BankAccName: row.BankAccName || '',
          EmirateId: '',
          EmirateName: row.EmirateName || row.Emirate || '',
          LocationId: '',
          LocationName: row.LocationName || row.Location || '',
          Locations: [],
          POBOX: row.POBOX || row.POBox || '',
          Address: row.Address || ''
        }));

        // Check emirates for each client
        for (const client of processedClients) {
          await checkEmirate(client);
        }

        setClients(processedClients);
        setSuccess(`Loaded ${processedClients.length} clients from Excel`);
      } catch (err) {
        console.error('Error parsing Excel:', err);
        setError('Failed to parse Excel file. Please check the format.');
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleClientFieldChange = async (index, field, value) => {
    setClients(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      
      if (field === 'EmirateId') {
        updated[index].LocationId = '';
        loadLocationsForClient(updated[index]);
      }
      
      return updated;
    });
  };

  const handleInit = () => {
    setClients([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClients = async () => {
    // Validation
    const invalidClients = clients.filter(client => 
      !client.EmirateId || !client.LocationId || !client.Name
    );

    if (invalidClients.length > 0) {
      setError('Invalid Format: Emirate, Location, and Name are required for all clients');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await MainService.SaveClientList(clients);

      if (response === 'Success') {
        setSuccess('Clients uploaded successfully');
        handleInit();
      } else {
        setError(response || 'Failed to upload clients');
      }
    } catch (err) {
      console.error('Error uploading clients:', err);
      setError('Failed to upload clients');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL || ''}/excel/BulkClient.xlsx`;
    link.download = 'BulkClient.xlsx';
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Client Upload</h1>
            <p className="text-sm text-gray-500 mt-1">Upload clients from Excel file</p>
          </div>
          <Button
            variant="outlined"
            startIcon={<FileDown className="w-4 h-4" />}
            onClick={downloadTemplate}
            sx={{
              borderColor: '#46955A',
              color: '#46955A',
              '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
            }}
          >
            Download Excel Template
          </Button>
        </div>

        {/* File Upload */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Choose Excel File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#46955A] text-sm"
            />
          </div>
        </div>
      </div>

      {/* Clients Table - Excel Style */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: '600px' }}>
          <table className="w-full border-collapse" style={{ minWidth: '1800px' }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '40px' }}>#</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Name</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Mobile No1</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Mobile No2</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Tel No</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Company</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Delivery Charges</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '180px' }}>Email Id</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Bank Name</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Bank Acc No</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Bank Acc Name</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '120px' }}>Emirate</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '150px' }}>Location</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '100px' }}>PO Box</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-xs font-semibold text-gray-700 bg-gray-100" style={{ minWidth: '200px' }}>Address</th>
              </tr>
            </thead>
            <tbody>
              {clients.length > 0 ? (
                clients.map((client, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-2 py-1 text-sm text-center bg-gray-50">{index + 1}</td>
                    <td className={`border border-gray-300 px-1 py-0 ${!client.Name ? 'bg-red-100' : ''}`}>
                      <input
                        type="text"
                        value={client.Name || ''}
                        onChange={(e) => handleClientFieldChange(index, 'Name', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '150px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.MobileNo1 || ''}
                        onChange={(e) => handleClientFieldChange(index, 'MobileNo1', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.MobileNo2 || ''}
                        onChange={(e) => handleClientFieldChange(index, 'MobileNo2', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.TelNo || ''}
                        onChange={(e) => handleClientFieldChange(index, 'TelNo', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.Company || ''}
                        onChange={(e) => handleClientFieldChange(index, 'Company', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '150px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.DeliveryCharges || ''}
                        onChange={(e) => handleClientFieldChange(index, 'DeliveryCharges', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '120px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="email"
                        value={client.EmailId || ''}
                        onChange={(e) => handleClientFieldChange(index, 'EmailId', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '180px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.BankName || ''}
                        onChange={(e) => handleClientFieldChange(index, 'BankName', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '150px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.BankAccNo || ''}
                        onChange={(e) => handleClientFieldChange(index, 'BankAccNo', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '150px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.BankAccName || ''}
                        onChange={(e) => handleClientFieldChange(index, 'BankAccName', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '150px' }}
                      />
                    </td>
                    <td className={`border border-gray-300 px-1 py-0 ${!client.EmirateId ? '' : 'bg-red-100'}`}>
                      {client.EmirateName && (
                        <div className="text-xs text-gray-500 mb-1 px-1">{client.EmirateName}</div>
                      )}
                      <select
                        value={client.EmirateId || ''}
                        onChange={(e) => handleClientFieldChange(index, 'EmirateId', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent"
                        style={{ minWidth: '120px' }}
                      >
                        <option value="">Select...</option>
                        {emirates.map((emi) => (
                          <option key={emi.Id} value={emi.Id}>{emi.Name}</option>
                        ))}
                      </select>
                    </td>
                    <td className={`border border-gray-300 px-1 py-0 ${!client.LocationId ? 'bg-red-100' : ''}`}>
                      {client.LocationName && (
                        <div className="text-xs text-gray-500 mb-1 px-1">{client.LocationName}</div>
                      )}
                      <select
                        value={client.LocationId || ''}
                        onChange={(e) => handleClientFieldChange(index, 'LocationId', e.target.value)}
                        disabled={!client.EmirateId}
                        className={`w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50 bg-transparent ${!client.EmirateId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        style={{ minWidth: '150px' }}
                      >
                        <option value="">Select...</option>
                        {client.Locations?.map((loc) => (
                          <option key={loc.Id} value={loc.Id}>{loc.Name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.POBOX || ''}
                        onChange={(e) => handleClientFieldChange(index, 'POBOX', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '100px' }}
                      />
                    </td>
                    <td className="border border-gray-300 px-1 py-0">
                      <input
                        type="text"
                        value={client.Address || ''}
                        onChange={(e) => handleClientFieldChange(index, 'Address', e.target.value)}
                        className="w-full px-2 py-1 text-sm border-0 outline-none focus:bg-blue-50"
                        style={{ minWidth: '200px' }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={15} className="border border-gray-300 px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <Upload className="w-12 h-12 mb-3 opacity-50" />
                      <p className="text-sm font-medium">No clients loaded</p>
                      <p className="text-xs mt-1">Upload an Excel file to get started</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-center gap-3">
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} sx={{ color: '#46955A' }} />
              <span className="text-sm text-gray-600">Processing...</span>
            </Box>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<RefreshCw className="w-4 h-4" />}
                onClick={handleInit}
                sx={{
                  borderColor: '#46955A',
                  color: '#46955A',
                  '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' }
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Upload className="w-4 h-4" />}
                onClick={handleUploadClients}
                disabled={clients.length === 0}
                sx={{
                  backgroundColor: '#46955A',
                  '&:hover': { backgroundColor: '#3d7d4f' },
                  '&:disabled': { backgroundColor: '#9CA3AF' }
                }}
              >
                Upload Clients
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientUpload;

